import React from 'react';
import { ToggleProps } from '../../../types/DesignSystem/Toggle';
import Typography from '../Typography/Typography';
import { FOCUS_RING } from '../focusRing';
import { Check, Cross } from '../Iconography/Icons';
import { withClassName } from '../cn';
import { useControllableState } from '../useControllableState';

/*
 * § 303 — the geometry per rung. `md` is, class for class, what the switch always
 * rendered (the pins in toggleState/interactionPolish read these exact strings); `sm`
 * is the same construction one rung down: a 36×20 track, the SAME 16px knob (a smaller
 * knob would need a smaller shadow to read as a knob) at a 2px inset, so the travel is
 * 36 − 16 − 2 = 18px, and the press squish (16→20) pulls the checked knob back to 14px
 * so its right edge stays planted — the md arithmetic at the smaller numbers. Glyphs
 * drop to 10px and sit at (20 − 10) / 2 = 5px. Literals, because the class scanner
 * cannot see a template.
 */
const GEOMETRY = {
  md: {
    track: 'tw-w-11 tw-h-6',
    knob: 'tw-top-1 tw-w-4 tw-h-4',
    on: 'tw-translate-x-6',
    off: 'tw-translate-x-1',
    squishOn: 'motion-safe:group-active:tw-translate-x-5',
    glyph: 12,
    glyphPos: 'tw-top-1.5',
  },
  sm: {
    track: 'tw-w-9 tw-h-5',
    knob: 'tw-top-0.5 tw-w-4 tw-h-4',
    on: 'tw-translate-x-[18px]',
    off: 'tw-translate-x-0.5',
    squishOn: 'motion-safe:group-active:tw-translate-x-3.5',
    glyph: 10,
    glyphPos: 'tw-top-[5px]',
  },
} as const;

const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(function Toggle({
  label,
  hideLabel = false,
  checked: checkedProp,
  defaultChecked,
  onChange,
  disabled = false,
  labelPlacement = 'end',
  size = 'md',
  className = '',
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  ...rest
}, ref) {
  /*
    The switch is a `<button>` with its label as a SIBLING, so it names itself with
    `aria-labelledby`. It used to set `aria-label={label}` — the same string the
    label already displayed — which named it correctly but made the accessible name
    a second, independent copy of the visible one, free to diverge (WCAG 2.5.3).
    `aria-labelledby` points at the one that is actually on screen.
  */
  const uid = React.useId();
  const labelId = `${uid}-label`;
  /* § 382 — the switch's id, so the label can point at it; a caller's own `id` is kept. */
  const switchId = (rest.id as string | undefined) ?? `${uid}-switch`;
  const hasVisibleLabel = label.length > 0;
  const stacked = labelPlacement === 'top';
  const g = GEOMETRY[size];
  /* § 357 — uncontrolled only with `defaultChecked` and no `checked`. */
  const [checked, setChecked, uncontrolled] = useControllableState(checkedProp, defaultChecked, false);
  const labelNode = hasVisibleLabel ? (
    <Typography
      id={labelId}
      variant="body-primary"
      /* § 382 — a real `<label for>` the switch: clicking the words flips it, as a Checkbox's
         and a Radio's always did (a `<button>` is labelable, and a label activates it). It was
         a sibling paragraph, so the target was the 44 × 24 track alone. `aria-labelledby`
         below still names the switch by this same element, so the name does not change. */
      as="label"
      htmlFor={switchId}
      /* The label recedes with its control. Owning it here is what lets call sites
         drop the `tw-opacity-50` they were wrapping the whole row in — which
         multiplied against an already-faint track and took it to ~1.04:1. */
      className={`${disabled ? 'tw-text-ink-disabled' : 'tw-text-ink tw-cursor-pointer'}${
        /* § 355 — hidden, not removed: `aria-labelledby` still names the switch by it. */
        hideLabel ? ' tw-sr-only' : ''
      }`}
    >
      {label}
    </Typography>
  ) : null;
  return (
    <div
      className={withClassName(`tw-flex ${
        stacked ? 'tw-flex-col tw-items-start tw-gap-2' : 'tw-items-center tw-gap-3'
      }`, className)}
    >
      {stacked && labelNode}
      <button
        /* rest first — see the rule in Button.tsx. Spread after `onClick`, a
           caller's own handler would replace the toggle's, leaving a switch that
           looks alive and does nothing. */
        {...rest}
        ref={ref}
        id={switchId}
        type="button"
        onClick={(event) => {
          if (disabled) return;
          setChecked(!checked);
          onChange?.(!checked, event);
        }}
        disabled={disabled}
        /*
          TRACK TIERS (§ 50). Measured 2026-08-25: the old pair — checked
          `green-success-500` (2.66:1 on white), unchecked `grey-500` (2.64:1) —
          was **1.01:1 against each other**: contrast-identical states, nearly
          hue-identical under red-green colour-blindness, leaving knob position as
          the only working cue. ADS's fix, adopted: a DARK success track (theirs
          #5B7F24 at 4.66:1; ours `green-success-700`, 4.53:1) and a glyph inside
          the track. Unchecked sits on `grey-550` — the § 25 control tier — so
          both states clear SC 1.4.11's 3:1.

          DISABLED TRACKS (§ 123, 2026-08-28) — this reverses § 50's "exempt and
          stay". Measured: `surface-sunken` #f4f5f7 on a white card is **1.09:1**,
          and the knob is `surface` white, so it was 1.09:1 against its own track
          too. The pill did not read as a pill; only the glyph rendered. Exempt
          from SC 1.4.11 means not REQUIRED to reach 3:1 — it does not mean a
          control may be invisible, and a user still has to see that a switch
          exists and which way it is set.

          Both disabled states now rest on `ink-disabled` (#b5bcc8): **1.91:1**
          against the card, and the same against the white knob. Deliberately
          LIGHTER than the enabled unchecked track (`edge-strong`, 3.18:1), so
          "off" and "locked off" stay tellable apart — the thing that made the
          original 1.09:1 choice tempting, kept without the invisibility.

          Disabled is greyscale in BOTH states, hue dropped. That is not a loss of
          information here: this component was already built so state does not
          depend on colour — the knob moves and the glyph rides the side the knob
          has left (see below). Green on a locked switch would only say "enabled".
        */
        /*
          § 219 — the track sheds the system's last stock-black chrome. It wore
          `tw-shadow-sm/md` (Tailwind's black-based stops, not the ds ladder) and
          hovered by `tw-opacity-90` — an opacity hover washes the track TOWARD
          its ground, the opposite of feedback. The track is flat now (a filled
          pill needs no shadow; the knob carries the depth) and hover moves to
          real rungs: checked takes `tone-success-bold-hover` — this switch is
          the "first hovering consumer" the § 206 per-tone-hover deferral was
          waiting for — and unchecked steps `edge-strong` (grey-550) to its ramp
          neighbour `ink-tertiary` (grey-600), darker in light and lighter in
          dark, the fill-hover direction each theme already uses. Ink-as-track
          precedent: § 123's disabled track below.
        */
        /*
          `tw-group` carries the press to the knob (§ 223 squish below).
          `forced-colors:tw-border` (§ 224): Windows High Contrast strips the
          track's fill, so without a border the switch has no body at all there —
          border-color is forced to a system colour, so width+style is all it needs.
        */
        className={`tw-group tw-relative ${g.track} tw-rounded-full tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift tw-ease-swift tw-flex-shrink-0 forced-colors:tw-border ${FOCUS_RING} ${
          disabled
            ? 'tw-bg-ink-disabled tw-cursor-not-allowed'
            : checked
            ? 'tw-bg-tone-success-bold hover:tw-bg-tone-success-bold-hover tw-cursor-pointer'
            : 'tw-bg-edge-strong hover:tw-bg-ink-tertiary tw-cursor-pointer'
        }`}
        role="switch"
        /* While controlled, the caller's `checked` as passed — the attribute is what it was. */
        aria-checked={uncontrolled ? checked : checkedProp}
        aria-disabled={disabled}
        data-size={size}
        aria-labelledby={hasVisibleLabel && !ariaLabel ? labelId : undefined}
        /*
          Only reached when no label is rendered — a switch in a table cell whose
          label lives in another column. The `?? label` keeps that path behaving
          exactly as before rather than quietly changing it, but note what it
          actually produces there: `label` is '' in that case, so the switch gets
          `aria-label=""` and therefore **no accessible name at all**. That gap
          predates this change and is the caller's to close by passing `aria-label`;
          it is written down here rather than silently preserved.
        */
        aria-label={ariaLabel ?? (hasVisibleLabel ? undefined : label)}
        data-testid={dataTestId}
      >
        {/*
          State without colour (SC 1.4.1), the ADS pattern: the glyph rides the
          side the knob has LEFT — check on the left when on, cross on the right
          when off — so the knob never covers it. `aria-hidden`: role="switch" +
          aria-checked already announce the state.

          The disabled glyph is `ink-secondary`, not `ink-disabled` (§ 123). It was
          `ink-disabled` to avoid white-on-a-pale-tint, but the track IS
          `ink-disabled` now, so the same token would have made glyph and track the
          identical colour — 1:1, gone. `ink-secondary` measures 3.76:1 on it.
        */}
        {checked ? (
          <Check
            size={g.glyph}
            aria-hidden="true"
            className={`tw-absolute tw-left-1 ${g.glyphPos} ${disabled ? 'tw-text-ink-secondary' : 'tw-text-ink-inverse'}`}
            data-testid={dataTestId ? `${dataTestId}-on-glyph` : undefined}
          />
        ) : (
          <Cross
            size={g.glyph}
            aria-hidden="true"
            className={`tw-absolute tw-right-1 ${g.glyphPos} ${disabled ? 'tw-text-ink-secondary' : 'tw-text-ink-inverse'}`}
            data-testid={dataTestId ? `${dataTestId}-off-glyph` : undefined}
          />
        )}
        {/* Knob depth from the ds ladder (§ 219), not the inline rgba(0,0,0,.25)
            it hardcoded — that literal could not theme and out-darked every other
            shadow in the system. Disabled recedes to flat: a locked switch casts
            nothing. `ease-swift` is what makes the slide settle instead of stop. */}
        {/*
          § 223 — the press SQUISH: while the button is held, the knob stretches
          16→20px anchored to the side it rests on (the iOS switch's signature).
          Width grows rightward from the left edge, which is already the direction
          of travel when unchecked; checked pulls translate back 4px so the RIGHT
          edge stays planted. `transition-all` (was -transform) is what tweens the
          width — spelled bare, not `transition-[transform,width]`: a comma inside
          an arbitrary value does not compile under a variant (the motion.ts
          lesson). The squish classes are motion-safe — under reduced motion the
          knob neither stretches nor tweens, it just lands. A disabled button
          receives no :active, so the guard is the browser's.
          `forced-colors:tw-border` (§ 224): the knob's white fill is stripped in
          Windows High Contrast; the border is what keeps a knob visible there.
        */}
        <div
          data-testid={dataTestId ? `${dataTestId}-knob` : undefined}
          className={`tw-absolute ${g.knob} tw-bg-surface tw-rounded-full tw-transition-all motion-safe:tw-duration-fast motion-safe:tw-ease-swift tw-ease-swift forced-colors:tw-border ${
            disabled ? '' : 'tw-shadow-ds-sm motion-safe:group-active:tw-w-5'
          } ${
            checked
              ? `${g.on}${disabled ? '' : ` ${g.squishOn}`}`
              : g.off
          }`}
        />
      </button>
      {/* Guarded like TextField and Radio: an empty label rendered an empty <p>
          plus this row's tw-gap-3, which shifts layout wherever the switch sits in
          a table cell with its label in another column. `label` still supplies the
          accessible name via aria-label above. */}
      {!stacked && labelNode}
    </div>
  );
});

export default Toggle;
